/** @file World bounds of bones (joints and tips) for frame-selection, each axis at least 64 units. */
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import type { Pose, SkeletonData } from '@pose2d/core/types';

const W = POSE_LAYOUT.WORLD_STRIDE;
const MIN_HALF = 32;

/** [lo, hi] of one axis, grown around its centre to at least 2 * MIN_HALF. */
function span(v: number[]): [number, number] {
  const lo = Math.min(...v), hi = Math.max(...v), mid = (lo + hi) / 2, half = Math.max((hi - lo) / 2, MIN_HALF);
  return [mid - half, mid + half];
}

/** [minX, minY, maxX, maxY], or null for no bones; a tiny box grows around its centre. */
export function frameBounds(pose: Pose, data: SkeletonData, bones: readonly number[]): number[] | null {
  if (bones.length === 0) return null;
  const xs: number[] = [];
  const ys: number[] = [];
  for (const i of bones) {
    const [a = 1, , c = 0, , tx = 0, ty = 0] = pose.world.subarray(i * W, i * W + W);
    const len = data.bones[i]?.length ?? 0;
    xs.push(tx, tx + a * len);
    ys.push(ty, ty + c * len);
  }
  const [x0, x1] = span(xs);
  const [y0, y1] = span(ys);
  return [x0, y0, x1, y1];
}
